import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ORD_History } from '../../interfaces/request.interface';

interface HistoryRow {
  id?: number;
  fieldName: string;
  oldValue: string;
  newValue: string;
  userName: string;
  modifiedDate: Date;
  displayDate: string;
  raw: ORD_History;
}

@Component({
    selector: 'app-history-data',
    templateUrl: './history-data.component.html',
    styleUrl: './history-data.component.css',
    standalone: false
})
export class HistoryDataComponent implements OnInit, OnChanges {
  @Input() history?: ORD_History[];

  public rows: HistoryRow[] = [];
  public filteredRows: HistoryRow[] = [];
  public searchTerm = '';
  public totalChanges = 0;
  public lastUpdated?: Date;

  ngOnInit(): void {
    this.buildRows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['history']) {
      this.buildRows();
    }
  }

  public onSearch(term: string): void {
    this.searchTerm = term ?? '';
    this.applyFilters();
  }

  public clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    this.applyFilters();
  }

  public trackRowBy(_: number, row: HistoryRow): string {
    const idPart = row.id ?? 'new';
    return `${idPart}_${row.modifiedDate.toISOString()}`;
  }

  private buildRows(): void {
    const items = [...(this.history ?? [])]
      .sort((a, b) => this.getDateValue(b.modifiedDate) - this.getDateValue(a.modifiedDate));

    this.totalChanges = items.length;
    this.lastUpdated = items.length
      ? this.toLocalDate(new Date(items[0].modifiedDate ?? new Date()))
      : undefined;

    this.rows = items.map(item => this.toHistoryRow(item));
    this.applyFilters();
  }

  private applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredRows = [...this.rows];
      return;
    }

    this.filteredRows = this.rows.filter(row => {
      const haystack = [
        row.fieldName,
        row.oldValue,
        row.newValue,
        row.userName,
        row.displayDate
      ].join(' ').toLowerCase();

      return haystack.includes(term);
    });
  }

  private toHistoryRow(item: ORD_History): HistoryRow {
    const changeDateUtc = item.modifiedDate ? new Date(item.modifiedDate) : new Date();
    const localChangeDate = this.toLocalDate(changeDateUtc);
    const userName = item?.userDisplayName || item?.loginID || 'System';
    const fieldName = item.characteristic || 'Unknown Field';

    const oldValue = this.formatValue(item.oldVal);
    const newValue = this.formatValue(item.newVal);

    return {
      id: item.id,
      fieldName,
      oldValue,
      newValue,
      userName,
      modifiedDate: localChangeDate,
      displayDate: localChangeDate.toLocaleString(),
      raw: item
    };
  }

  private formatValue(value: string | undefined): string {
    if (value == null || value === '') {
      return '—';
    }
    return value;
  }

  private getDateValue(value?: Date): number {
    return value ? new Date(value).getTime() : 0;
  }

  private toLocalDate(date: Date): Date {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return new Date();
    }
    const localTime = date.getTime() - date.getTimezoneOffset() * 60000;
    return new Date(localTime);
  }
}
